import { logError } from 'src/utils/logger';

import pdfProcessingManager from '../utils/pdf-processing-manager.js';

import UserInfoFactory from './user-info';

let projectData;
const resources = {};
let cache = {};
const cachedDomains = [
  'desygner',
  'wattpad-master'
];

const cacheUrl = (INKIVE.C.IS_DEV || INKIVE.C.IS_QA)
  ? 'https://static.qadesygner.xyz/'
  : 'https://static.desygner.com/';

function CompanyFactory (domain) {
  this.domain = domain;

  if (domain) {
    if (!CompanyFactory.externalInstances[domain]) {
      CompanyFactory.externalInstances[domain] = this;
    }
    return CompanyFactory.externalInstances[domain];
  }
}

CompanyFactory.externalInstances = {};

CompanyFactory.prototype.initialize = _.memoize(function (domain) {
  this.domain = domain;

  return this.initCache()
    .then(() => this.initCredentials())
    .then(() => this.initProject());
}, function (domain) {
  return domain;
});

CompanyFactory.prototype.initCache = function () {
  const deferred = $.Deferred();
  const cachedDomain = cachedDomains.includes(this.domain)
    ? this.domain
    : 'desygner';

  const getCacheVersions = (domain) => {
    const cacheVersionsUrl = 'api/brand/companies/' + domain + '/version';
    const url = cacheUrl + cacheVersionsUrl;

    return window.fetch(url);
  };

  if (this.cachedVersions) {
    deferred.resolve();
  } else {
    getCacheVersions(cachedDomain)
      .then(response => response.json())
      .then((data) => {
        this.cachedVersions = data;
        deferred.resolve();
        return data;
      })
      .catch(deferred.resolve);
  }

  return deferred.promise();
};

CompanyFactory.prototype.initCredentials = function () {
  const deferred = $.Deferred();

  UserInfoFactory.get().then((data) => {
    this.credentials = {
      username: data.credentials.token,
      password: data.credentials.hash
    };

    deferred.resolve();
  });

  return deferred.promise();
};

CompanyFactory.prototype.initProject = function (force) {
  const deferred = $.Deferred();

  if (!cache.companyData) {
    cache.companyData = this.fetchProjectData();

    cache.companyData.then((data) => {
      data.is_being_processed && pdfProcessingManager.subscribe(data);
      if (!projectData?.company) logError('Company data is not available', projectData);
      this.companyId = CONFIG.company_id = projectData.company.id;
      CONFIG.company_name = projectData.company.name;
      CONFIG.company_domain = projectData.company.domain;
      INKIVE.Metadata = projectData?.metadata || {};

      deferred.resolve(data);
    }, function () {
      deferred.reject(new Error('Failed to fetch project data'));
    });

    return deferred.promise();
  }

  return cache.companyData;
};

CompanyFactory.prototype.fetchProjectData = function () {
  return INKIVE.PageFactory.getProjectData().then(data => {
    projectData = {
      ...projectData,
      ...data
    };

    return data;
  }).catch(logError('fetchProjectData'));
};

CompanyFactory.prototype.getCompanyData = function () {
  const deferred = $.Deferred();

  this._get('').then((data) => {
    deferred.resolve(data && data[0]);
  }, function () {
    deferred.reject(new Error('Failed to fetch company data'));
  });

  return deferred.promise();
};

CompanyFactory.prototype.getCompanyItems = function (items) {
  const deferred = $.Deferred();

  this.getCompanyData()
    .then((data) => {
      deferred.resolve(data && data[items]);
    }, function () {
      deferred.reject(new Error('Failed to fetch company items'));
    });

  return deferred.promise();
};

CompanyFactory.prototype._undraft = async function () {
  return await this.setProjectData({ is_draft: false });
};

// TODO: get this out of here, this should be a much highr level (maybe editor facade?)
CompanyFactory.prototype.initialTemplateResize = function (format, name) {
  const DesignFacade = require('../facade/design/design');
  const EditorFacade = require('../facade/editor/editor');

  let alert,
    projectFormat;
  const totalPages = INKIVE.C.BOOK.num_pages;

  format = _.isNumber(format)
    ? format
    : format.id;

  showMessage();

  this._get('formats', format).then((pf) => {
    projectFormat = pf;

    INKIVE.Tool._current_page_number = 0;
    resizeNext.call(this);
  });

  function resizeNext () {
    INKIVE.hasEditorUnsavedChanges = true;
    if (INKIVE.Tool._current_page_number < totalPages) {
      EditorFacade.handle('page', 'move_to', {
        page_number: INKIVE.Tool._current_page_number + 1
      }).then(() => {
        document.querySelector('#svgcontent').classList.add('template-resizing');
        showMessage();
        resizeCurrent.call(this);
      });
    } else {
      onceDone.call(this);
    }
  }

  function onceDone () {
    const justOnePage = totalPages === 1;

    const _afterFinishActions = _.after(2, afterFinishActions);

    _afterFinishActions();
    name = name + '_' + projectFormat.name + '_Print';
    INKIVE.Helpers.isDesygnerProject() && EditorFacade.handle('name_change', { name });

    justOnePage && EditorFacade.handle('page', 'save')
      .then(_afterFinishActions);

    justOnePage || EditorFacade.handle('page', 'move_to', {
      page_number: 1
    }).then(_afterFinishActions);

    function afterFinishActions () {
      document.querySelector('#svgcontent').classList.remove('template-resizing');
      INKIVE.ProjectFacade.refreshPageInfo();
      INKIVE.Modal.toggleLock(false);
      alert.close();
    }
  }

  function resizeCurrent () {
    DesignFacade.once('template.update', () => {
      resizeNext.call(this);
    });
    DesignFacade.set('template', { size: projectFormat, skipRemoveRestrictions: true });
  }

  function showMessage () {
    function show () {
      INKIVE.Modal.toggleLock(false);

      const page = INKIVE.Tool._current_page_number || 1;
      const message = [
        '<b style="font-weight: 400">',
        INKIVE.TranslateHelper.translate('modal.alert.resize_page', {
          page,
          from: totalPages,
          percent: Math.floor(((page - 1) / totalPages) * 100)
        }),
        '</b>'
      ];

      alert = INKIVE.Modal.alertMessage(message.join(''), {
        skip: false,
        loader: true
      });

      INKIVE.Modal.toggleLock(true);
    }

    alert && alert.close(show);
    alert || show();
  }
};

CompanyFactory.prototype.getCompany = function () {
  const deferred = $.Deferred();
  const isCompanyDesign = INKIVE.Helpers.isWeBrandProject();

  isCompanyDesign || deferred.resolve();
  isCompanyDesign && this.initialize(this.domain).then(function () {
    deferred.resolve(projectData.company);
  }, function () {
    deferred.resolve();
  });

  return deferred.promise();
};

CompanyFactory.prototype.getProjectData = function () {
  const deferred = $.Deferred();
  const isCompanyDesign = INKIVE.Helpers.isWeBrandProject();

  isCompanyDesign
    ? this.initialize(this.domain).then(function () {
      deferred.resolve(projectData);
    }, function () {
      deferred.resolve();
    })
    : deferred.resolve();

  return deferred.promise();
};

CompanyFactory.prototype.setProjectData = function (data) {
  const deferred = $.Deferred();

  this.getProjectData().then(() => {
    const hasChanges = _.any(data, function (value, field) {
      return value !== projectData[field];
    });

    if (hasChanges) {
      Object.assign(projectData, data);

      INKIVE.C.BOOK.book_type === INKIVE.C.BOOK_TYPE.TEMPLATE
        ? INKIVE.PageFactory.saveTemplateData(projectData)
        : INKIVE.PageFactory.saveDesignData(projectData);
    }

    deferred.resolve(projectData);
  });

  return deferred.promise();
};

CompanyFactory.prototype.dropCache = function (resourceName, id) {
  const resourceUrl = this.getResourceUrl(arguments);
  cache = _(cache).omit(resourceUrl);
};

CompanyFactory.prototype.get = function (path, noCache) {
  const [resourceName, id] = path;
  if (['templates'].includes(resourceName) && id === INKIVE.C.BOOK.id && !noCache) {
    return this.getProjectData();
  }

  return this.initialize(this.domain).then(() => {
    this.noCache = noCache;
    return this._get.apply(this, !Array.isArray(path) ? [path] : path);
  });
};

CompanyFactory.prototype._get = function (resourceName, id) {
  const deferred = $.Deferred();

  const resourceUrl = this.getResourceUrl(arguments);
  const resource = this.getContainer.apply(this, arguments);

  if (!cache[resourceUrl] || this.noCache) { cache[resourceUrl] = resource.fetch(); }

  cache[resourceUrl].then(function () {
    deferred.resolve(resource.toJSON());
  }, function () {
    deferred.reject(new Error(`Failed to fetch resource: ${resourceUrl}`));
  });

  return deferred.promise();
};

CompanyFactory.prototype.getContainer = function (resourceName, id) {
  const resourceUrl = this.getResourceUrl(arguments);
  const isCollection = arguments.length % 2;
  const resource = resources[resourceUrl]
    ? resources[resourceUrl]
    : this.createResourceContainer(resourceUrl, isCollection);

  return resource;
};

CompanyFactory.prototype.getResourceUrl = function (route) {
  const domain = this.domain || projectData.company.domain;
  const endpoint = 'brand/companies/' + domain;

  route = Array.from(route);

  for (const i in route) {
    if (typeof route[i] === 'undefined') {
      console.error('Trying to load a broken path on company API: ' + endpoint + '/' + route.join('/'));
      break;
    }
  }

  return this.getEndpointUrl(route.length === 1 && route[0] === ''
    ? endpoint
    : endpoint + '/' + route.join('/')
  );
};

CompanyFactory.prototype.getEndpointUrl = function (endpoint) {
  const domainEndpoint = 'api/' + endpoint.replace('companies/' + projectData.company.id + '/',
    'companies/' + this.domain + '/');

  if (!this.cachedVersions || !this.cachedVersions[domainEndpoint]) {
    return INKIVE.C.API_URL + endpoint;
  }

  const { key } = this.cachedVersions[domainEndpoint];
  return cacheUrl + key;
};

CompanyFactory.prototype.setCredentials = function (credentials) {
  this.credentials = credentials;
};

CompanyFactory.prototype.createResourceContainer = function (url, isCollection) {
  url = url.includes(INKIVE.C.API_URL)
    ? url
    : INKIVE.Helpers.getCORSSecureUrl(url);

  let Container;

  if (url.indexOf(INKIVE.C.WE_BRAND) === 0 || url.indexOf(INKIVE.C.API_URL) === 0) {
    Container = isCollection
      ? Backbone.Collection.extend({
        url,
        credentials: this.credentials,
        model: Backbone.Model.extend({
          credentials: this.credentials
        })
      })
      : Backbone.Model.extend({
        url,
        credentials: this.credentials
      });
  } else {
    Container = isCollection
      ? Backbone.Collection.extend({ url })
      : Backbone.Model.extend({ url });
  }

  const container = new Container();

  resources[url] = container;

  return container;
};

export const factory = CompanyFactory;
export const instance = new CompanyFactory();

module.exports = { factory, instance };
